import { useEffect, useRef, useState, useCallback } from 'react';
import { useAvatarSprites } from '@/hooks/useAvatarSprites';
import { useRoverSprites } from '@/hooks/useRoverSprites';
import './StoreMap.css';

interface Room {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: 'rover_sales' | 'food' | 'energy' | 'water' | 'mining' | 'helium3' | 'nova_lounge';
}

// Rover data passed when exiting store
export interface RoverData {
  hasRover: boolean;
  outfitType: string | null;
  money: number; // Updated money after purchases
  exitDirection?: 'top' | 'bottom' | 'left' | 'right'; // Direction player exited from
}

interface StoreMapProps {
  isOpen: boolean;
  gameId: string; // Required for backend API calls
  playerMoney: number;
  playerRovers: number;
  currentPlayerNumber: number;
  timeRemaining: number; // Seconds remaining in turn
  turnDuration: number; // Total turn duration in seconds (for progress bar)
  entryDirection?: 'top' | 'bottom' | 'left' | 'right' | null; // Direction player entered from
  onClose: (exitDirection?: 'top' | 'bottom' | 'left' | 'right') => void; // Called when exiting without rover
  onExitWithRover: (roverData: RoverData) => void; // Called when exiting with rover to deploy
  onEnterRoom: (roomType: Room['type']) => void;
  onNovaLounge?: () => void;
  carriedMule?: { outfitType: string | null } | null;
  mulesInStore?: number;
  // Attached rover (from rover replacement - player can deploy elsewhere or sell)
  attachedRover?: { outfitType: string; previousPlotId?: string } | null;
  onRoverSold?: (newMoney: number) => void; // Called when player sells their attached rover
  onCarriedChange?: (mule: { outfitType: string | null } | null) => void;
  // AI spectating props
  isAITurn?: boolean; // True when watching an AI player
  aiPlayerName?: string; // Name of AI player being watched
  aiAction?: string; // Current AI action phase for visualization
  aiOutfitType?: string; // Outfit type AI is choosing
  aiRoverCycle?: number; // Current rover cycle (1-based, for multi-rover support)
}

// Store dimensions
const STORE_WIDTH = 800;
const STORE_HEIGHT = 600;
const FLOOR_MARGIN = 60;

// Room dimensions
const ROOM_WIDTH = 140;
const ROOM_HEIGHT = 80;
const WALL_GAP = 10;

// Avatar settings
const AVATAR_SIZE = 32;
const AVATAR_SPEED = 3; // pixels per frame

// Colors
const COLORS = {
  background: '#0A0E27',
  floor: '#162142',
  wall: '#4A90E2',
  door: '#00FF88',
  roomBg: '#0F3460',
  text: '#FFFFFF',
  interact: '#FFC107',
};

// Store costs (matching backend)
const ROVER_BASE_COST = 100; // CT
const OUTFIT_COSTS: Record<string, number> = {
  energy: 25,
  water: 35,
  mining: 40,
  helium3: 45,
  food: 50,
};

// Define all 7 rooms with their positions (optimized spacing for exits)
const ROOMS: Room[] = [
  // LEFT WALL (4 rooms) - Better spacing, Energy closer to Rover Sales, Water closer to Nova Lounge
  {
    id: 'rover_sales',
    name: 'ROVER\nSALES',
    x: FLOOR_MARGIN + WALL_GAP,
    y: FLOOR_MARGIN + WALL_GAP,
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'rover_sales',
  },
  {
    id: 'energy',
    name: 'ENERGY\nOUTFIT',
    x: FLOOR_MARGIN + WALL_GAP,
    y: FLOOR_MARGIN + WALL_GAP + ROOM_HEIGHT + 15, // Closer to Rover Sales (15px gap instead of 50)
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'energy',
  },
  {
    id: 'water',
    name: 'WATER\nOUTFIT',
    x: FLOOR_MARGIN + WALL_GAP,
    y: STORE_HEIGHT - FLOOR_MARGIN - WALL_GAP - ROOM_HEIGHT * 2 - 15, // Closer to Nova Lounge
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'water',
  },
  {
    id: 'helium3',
    name: 'HELIUM-3\nOUTFIT',
    x: FLOOR_MARGIN + WALL_GAP,
    y: STORE_HEIGHT - FLOOR_MARGIN - WALL_GAP - ROOM_HEIGHT, // Bottom left (swapped with Nova Lounge)
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'helium3',
  },
  // RIGHT WALL (3 rooms) - Better spacing, Mining closer to Food, Nova Lounge moved to bottom right
  {
    id: 'food',
    name: 'FOOD\nOUTFIT',
    x: STORE_WIDTH - FLOOR_MARGIN - WALL_GAP - ROOM_WIDTH,
    y: FLOOR_MARGIN + WALL_GAP,
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'food',
  },
  {
    id: 'mining',
    name: 'MINING\nOUTFIT',
    x: STORE_WIDTH - FLOOR_MARGIN - WALL_GAP - ROOM_WIDTH,
    y: FLOOR_MARGIN + WALL_GAP + ROOM_HEIGHT + 15, // Closer to Food (15px gap instead of 50)
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'mining',
  },
  {
    id: 'nova_lounge',
    name: 'NOVA\nLOUNGE',
    x: STORE_WIDTH - FLOOR_MARGIN - WALL_GAP - ROOM_WIDTH,
    y: STORE_HEIGHT - FLOOR_MARGIN - WALL_GAP - ROOM_HEIGHT, // Bottom right (swapped with Helium-3)
    width: ROOM_WIDTH,
    height: ROOM_HEIGHT,
    type: 'nova_lounge',
  },
];

// Exit doors (4 sides)
const EXITS = [
  { name: 'top', x: (STORE_WIDTH - 80) / 2, y: 20, width: 80, height: 40 },
  { name: 'bottom', x: (STORE_WIDTH - 80) / 2, y: STORE_HEIGHT - 60, width: 80, height: 40 },
  { name: 'left', x: 20, y: (STORE_HEIGHT - 80) / 2, width: 40, height: 80 },
  { name: 'right', x: STORE_WIDTH - 60, y: (STORE_HEIGHT - 80) / 2, width: 40, height: 80 },
];

const StoreMap: React.FC<StoreMapProps> = ({
  isOpen,
  gameId,
  playerMoney,
  playerRovers,
  currentPlayerNumber,
  timeRemaining,
  turnDuration,
  entryDirection = null,
  onClose,
  onExitWithRover,
  onEnterRoom,
  onNovaLounge,
  carriedMule = null,
  mulesInStore = 4,
  // Attached rover (can sell or redeploy)
  attachedRover = null,
  onRoverSold,
  onCarriedChange,
  // AI spectating props
  isAITurn = false,
  aiPlayerName = 'AI Player',
  aiAction = '',
  aiOutfitType = '',
  aiRoverCycle = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  // Calculate initial avatar position based on entry direction
  const getInitialPosition = () => {
    const safeMargin = FLOOR_MARGIN + AVATAR_SIZE / 2 + 10;
    switch (entryDirection) {
      case 'top':
        return { x: STORE_WIDTH / 2, y: safeMargin };
      case 'bottom':
        return { x: STORE_WIDTH / 2, y: STORE_HEIGHT - safeMargin };
      case 'left':
        return { x: safeMargin, y: STORE_HEIGHT / 2 };
      case 'right':
        return { x: STORE_WIDTH - safeMargin, y: STORE_HEIGHT / 2 };
      default:
        return { x: STORE_WIDTH / 2, y: STORE_HEIGHT / 2 }; // Center if no direction
    }
  };
  
  // Avatar position (based on entry direction)
  const [avatarPos, setAvatarPos] = useState(getInitialPosition);
  const [avatarDirection, setAvatarDirection] = useState<'up' | 'down' | 'left' | 'right'>('down');
  const [isWalking, setIsWalking] = useState(false);
  
  const carriedOnOpen = useRef(carriedMule);
  carriedOnOpen.current = carriedMule;

  // Reset avatar position when store opens with new entry direction.
  // A mule is shown only when one was already bought and is still being carried.
  useEffect(() => {
    if (isOpen) {
      const initialPos = getInitialPosition();
      const carried = carriedOnOpen.current;
      console.log('🏪 Store opened, setting avatar position based on entry:', entryDirection, initialPos);
      setAvatarPos(initialPos);
      if (carried) {
        setHasRover(true);
        setRoverOutfitType(carried.outfitType);
        setRoverPos({ x: initialPos.x, y: initialPos.y - 36 });
      } else {
        setHasRover(false);
        setRoverOutfitType(null);
      }
      
      // Set initial direction to face into the store
      switch (entryDirection) {
        case 'top':
          setAvatarDirection('down');
          break;
        case 'bottom':
          setAvatarDirection('up');
          break;
        case 'left':
          setAvatarDirection('right');
          break;
        case 'right':
          setAvatarDirection('left');
          break;
        default:
          setAvatarDirection('down');
      }
    }
  }, [isOpen, entryDirection]);
  
  // AI player automatic movement based on aiAction phase
  useEffect(() => {
    if (!isAITurn || !isOpen) return;
    
    // Find target room/position based on AI action
    const roverSalesRoom = ROOMS.find(r => r.id === 'rover_sales');
    const outfitRoom = aiOutfitType ? ROOMS.find(r => r.id === aiOutfitType) : null;
    const bottomExit = EXITS.find(e => e.name === 'bottom');
    
    let targetX = STORE_WIDTH / 2;
    let targetY = STORE_HEIGHT / 2;
    
    // Find Nova Lounge room
    const novaLoungeRoom = ROOMS.find(r => r.id === 'nova_lounge');
    
    switch (aiAction) {
      case 'entering_store':
        // Move to center from entry point
        targetX = STORE_WIDTH / 2;
        targetY = STORE_HEIGHT / 2;
        break;
      case 'buying_rover':
        // Move to rover sales room
        if (roverSalesRoom) {
          targetX = roverSalesRoom.x + roverSalesRoom.width + 40; // Stand in front of room
          targetY = roverSalesRoom.y + roverSalesRoom.height / 2;
        }
        break;
      case 'outfitting':
        // Move to outfit room
        if (outfitRoom) {
          // Stand in front of the room (left rooms vs right rooms)
          if (outfitRoom.x < STORE_WIDTH / 2) {
            // Left side room - stand to the right of it
            targetX = outfitRoom.x + outfitRoom.width + 40;
          } else {
            // Right side room - stand to the left of it
            targetX = outfitRoom.x - 40;
          }
          targetY = outfitRoom.y + outfitRoom.height / 2;
        }
        break;
      case 'exiting_store':
        // Move to exit
        if (bottomExit) {
          targetX = bottomExit.x + bottomExit.width / 2;
          targetY = STORE_HEIGHT - FLOOR_MARGIN - AVATAR_SIZE;
        }
        break;
      case 'returning_to_store':
        // Move back into store center (entry point)
        targetX = STORE_WIDTH / 2;
        targetY = STORE_HEIGHT - FLOOR_MARGIN - AVATAR_SIZE - 50;
        break;
      case 'visiting_lounge':
        // Move to Nova Lounge room
        if (novaLoungeRoom) {
          // Stand in front of the lounge (it's on the right side)
          targetX = novaLoungeRoom.x - 40;
          targetY = novaLoungeRoom.y + novaLoungeRoom.height / 2;
        }
        break;
      default:
        // Stay in center
        break;
    }
    
    console.log(`🤖 AI action: ${aiAction} -> moving to (${targetX}, ${targetY})`);
    setTargetPos({ x: targetX, y: targetY });
    targetPosRef.current = { x: targetX, y: targetY };
    targetRoomRef.current = null;
    targetExitRef.current = null;
    
    // Simulate rover state based on AI action
    // AI has rover only during these phases - NOT when returning to store or in lounge
    if (aiAction === 'entering_store' || aiAction === 'buying_rover') {
      // Starting a new cycle or walking to buy - no rover yet
      setHasRover(false);
      setRoverOutfitType(null);
    } else if (aiAction === 'outfitting' || aiAction === 'exiting_store' || aiAction === 'walking_to_plot') {
      // Rover only appears AFTER purchase (during outfitting, exiting, or walking to plot)
      setHasRover(true);
      if (aiAction === 'outfitting' || aiAction === 'exiting_store' || aiAction === 'walking_to_plot') {
        setRoverOutfitType(aiOutfitType || 'mining');
      }
    } else if (aiAction === 'returning_to_store' || aiAction === 'visiting_lounge' || aiAction === 'done') {
      // Rover has been deployed - AI no longer has it
      setHasRover(false);
      setRoverOutfitType(null);
    }
  }, [isAITurn, isOpen, aiAction, aiOutfitType]);
  
  // Click-to-move target
  const [targetPos, setTargetPos] = useState<{ x: number; y: number } | null>(null);
  const targetPosRef = useRef<{ x: number; y: number } | null>(null); // Synchronous version for immediate checks
  const targetRoomRef = useRef<string | null>(null); // Track which room was clicked
  const targetExitRef = useRef<string | null>(null); // Track which exit was clicked
  
  // Rover state (only visible after purchase)
  const [hasRover, setHasRover] = useState(carriedMule != null);
  const [roverOutfitType, setRoverOutfitType] = useState<string | null>(carriedMule?.outfitType ?? null);
  const [shownTime, setShownTime] = useState(timeRemaining);
  const clockAnchor = useRef({ remaining: timeRemaining, at: Date.now() });

  useEffect(() => {
    clockAnchor.current = { remaining: timeRemaining, at: Date.now() };
    setShownTime(timeRemaining);
  }, [timeRemaining]);

  useEffect(() => {
    setMulesLeft(mulesInStore);
  }, [mulesInStore]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const left = clockAnchor.current.remaining - (Date.now() - clockAnchor.current.at) / 1000;
      setShownTime(Math.max(0, left));
    }, 200);
    return () => window.clearInterval(id);
  }, []);
  const [roverPos, setRoverPos] = useState({ x: STORE_WIDTH / 2 + 35, y: STORE_HEIGHT / 2 + 5 });
  const [mulesLeft, setMulesLeft] = useState(mulesInStore);
  const [notice, setNotice] = useState<string | null>(null);
  
  // Local money tracking. A poll can change the wallet after the store opens.
  const [currentMoney, setCurrentMoney] = useState(playerMoney);
  useEffect(() => {
    setCurrentMoney(playerMoney);
  }, [playerMoney]);
  useEffect(() => {
    setMulesLeft(mulesInStore);
  }, [mulesInStore]);

  const refreshWallet = useCallback(async () => {
    try {
      const { apiClient } = await import('@/services/api');
      const fresh = await apiClient.getGameState(gameId);
      const me = fresh.players?.find((player) => player.playerNumber === currentPlayerNumber);
      if (me && typeof me.money === 'number') setCurrentMoney(me.money);
      const corral = (fresh as { store?: { availableRovers?: number } }).store?.availableRovers;
      if (typeof corral === 'number') setMulesLeft(corral);
    } catch {
      // The next poll still corrects the wallet.
    }
  }, [currentPlayerNumber, gameId]);
  
  // Modal state for purchase/outfit/sell confirmations
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showOutfitModal, setShowOutfitModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellKind, setSellKind] = useState<'mule' | 'equipment'>('mule');
  const [selectedOutfitType, setSelectedOutfitType] = useState<string | null>(null);
  
  // Animation frame for sprite
  const [animationFrame, setAnimationFrame] = useState(0);
  const lastFrameTimeRef = useRef<number>(Date.now());
  
  
  // Load sprites
  const { avatarSprites, loading: spritesLoading } = useAvatarSprites();
  const { roverSprites, loading: roverSpritesLoading } = useRoverSprites();
  
  // Hover state for rooms
  const [hoveredRoom, setHoveredRoom] = useState<string | null>(null);
  
  /**
   * Handle rover purchase from backend
   */
  const handlePurchaseRover = async () => {
    if (mulesLeft <= 0) {
      setNotice('The store is out of mules.');
      setShowPurchaseModal(false);
      return;
    }
    if (currentMoney < ROVER_BASE_COST) {
      setNotice(`A mule costs ${ROVER_BASE_COST} CT.`);
      setShowPurchaseModal(false);
      return;
    }
    
    try {
      console.log('🚀 Purchasing rover from backend...');
      
      // Call backend API
      const { apiClient } = await import('@/services/api');
      const result = await apiClient.purchaseRover(gameId);
      
      if (result.success) {
        // Update local state from server response
        setHasRover(true);
        setCurrentMoney(result.playerMoney);
        onCarriedChange?.({ outfitType: null });
        
        // Set rover position to be right next to the avatar (behind and to the right)
        setRoverPos({ x: avatarPos.x + 35, y: avatarPos.y + 5 });
        
        setShowPurchaseModal(false);
        
        // Reset movement state to ensure avatar can move after purchase
        setTargetPos(null);
        targetPosRef.current = null;
        setIsWalking(false);
        
        console.log(`✅ Rover purchased for ${ROVER_BASE_COST} CT (server confirmed: ${result.playerMoney} CT remaining)`);
      } else {
        throw new Error('Purchase failed');
      }
    } catch (error: unknown) {
      const response = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const message = String(response || (error instanceof Error ? error.message : '') || 'Purchase failed')
        .replace(/^VALIDATION:\s*/i, '');
      setNotice(message);
      setShowPurchaseModal(false);
      await refreshWallet();
    }
  };
  
  /**
   * Handle rover outfit from backend
   */
  const handleOutfitRover = async (outfitType: string) => {
    const cost = OUTFIT_COSTS[outfitType];
    
    if (currentMoney < cost) {
      alert(`❌ Insufficient funds! Need ${cost} CT, have ${currentMoney} CT.`);
      setShowOutfitModal(false);
      return;
    }
    
    try {
      console.log(`🔧 Outfitting rover with ${outfitType}...`);
      
      // Check if re-outfitting
      if (roverOutfitType) {
        const confirmReoutfit = window.confirm(
          `⚠️ Rover is already outfitted with ${roverOutfitType.toUpperCase()}.\n\n` +
          `Re-outfit to ${outfitType.toUpperCase()} for ${cost} CT?`
        );
        
        if (!confirmReoutfit) {
          setShowOutfitModal(false);
          return;
        }
      }
      
      // Call backend API
      const { apiClient } = await import('@/services/api');
      const result = await apiClient.outfitRover(gameId, outfitType);
      
      if (result.success) {
        // Update local state from server response
        setRoverOutfitType(result.rover.outfitType);
        setCurrentMoney(result.playerMoney);
        onCarriedChange?.({ outfitType: result.rover.outfitType });
        if (result.storeInventory?.availableRovers != null) {
          setMulesLeft(result.storeInventory.availableRovers);
        }
        setShowOutfitModal(false);
        
        // Reset movement state to ensure avatar can move after outfitting
        setTargetPos(null);
        targetPosRef.current = null;
        setIsWalking(false);
        
        console.log(`✅ Rover outfitted with ${outfitType.toUpperCase()} for ${cost} CT (server confirmed: ${result.playerMoney} CT remaining)`);
      } else {
        throw new Error('Outfit failed');
      }
    } catch (error: unknown) {
      const response = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      const message = String(response || (error instanceof Error ? error.message : '') || 'Outfit failed')
        .replace(/^VALIDATION:\s*/i, '');
      setNotice(message);
      setShowOutfitModal(false);
    }
  };
  
  /**
   * Handle selling attached rover back to the store
   */
  const handleSellRover = async () => {
    try {
      const { apiClient } = await import('@/services/api');
      const sellingEquipment = sellKind === 'equipment';
      const result = await apiClient.sellRover(
        gameId,
        sellingEquipment ? { equipment: true, outfitType: roverOutfitType || undefined } : undefined,
      );
      if (!result.success) throw new Error('Sell failed');
      setCurrentMoney(result.playerMoney);
      if (result.storeInventory?.availableRovers != null) setMulesLeft(result.storeInventory.availableRovers);
      setShowSellModal(false);
      if (onRoverSold) onRoverSold(result.playerMoney);
      if (sellingEquipment) {
        setRoverOutfitType(null);
        onCarriedChange?.({ outfitType: null });
        setNotice(`Sold the equipment for ${result.price} CT. The mule is bare.`);
      } else {
        setHasRover(false);
        setRoverOutfitType(null);
        onCarriedChange?.(null);
        setNotice(`Sold the mule for ${result.price} CT.`);
      }
    } catch (error: any) {
      const errorMsg = String(error?.response?.data?.message || error?.message || 'Sell failed').replace(/^VALIDATION:\s*/i, '');
      setNotice(errorMsg);
      setShowSellModal(false);
    }
  };
  
  /**
   * Auto-interact with room when avatar reaches it
   */
  const handleRoomReached = useCallback((roomId: string) => {
    console.log('🚪 Avatar reached room:', roomId);
    
    // ALWAYS clear target room ref first
    targetRoomRef.current = null;
    
    // Find the room to calculate safe exit position
    const room = ROOMS.find(r => r.id === roomId);
    
    // Push avatar OUT of room to a safe position (just outside the entrance)
    // This prevents avatar from being stuck inside room collision box
    if (room) {
      // Calculate exit position (slightly outside room entrance)
      const roomCenterX = room.x + room.width / 2;
      const roomCenterY = room.y + room.height / 2;
      const storeCenterX = STORE_WIDTH / 2;
      const storeCenterY = STORE_HEIGHT / 2;
      
      // Push avatar towards store center (away from room)
      const dx = storeCenterX - roomCenterX;
      const dy = storeCenterY - roomCenterY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Find a safe exit position that doesn't collide
      let exitDistance = 80; // Start distance
      let exitX = roomCenterX + (dx / distance) * exitDistance;
      let exitY = roomCenterY + (dy / distance) * exitDistance;
      
      // If still colliding, move further towards center until clear
      let attempts = 0;
      while (checkCollision(exitX, exitY) && attempts < 10) {
        exitDistance += 20; // Move 20px further each attempt
        exitX = roomCenterX + (dx / distance) * exitDistance;
        exitY = roomCenterY + (dy / distance) * exitDistance;
        attempts++;
        console.log(`🔄 Exit position collides, trying ${exitDistance}px away...`);
      }
      
      console.log('🚶 Moving avatar out of room to:', { exitX, exitY, attempts });
      setAvatarPos({ x: exitX, y: exitY });
    }
    
    // Special handling for Rover Sales room
    if (roomId === 'rover_sales') {
      if (hasRover && roverOutfitType) {
        setNotice(`Sell the ${roverOutfitType} equipment in that room before selling the mule.`);
      } else if (hasRover) {
        setSellKind('mule');
        setShowSellModal(true);
      } else if (attachedRover?.outfitType) {
        setNotice(`Sell the ${attachedRover.outfitType} equipment in that room before selling the mule.`);
      } else {
        setShowPurchaseModal(true);
        void refreshWallet();
      }
    }
    else if (['energy', 'water', 'mining', 'helium3', 'food'].includes(roomId)) {
      if (!hasRover) {
        setNotice('Buy a mule at Rover Sales first.');
      } else if (roverOutfitType && roverOutfitType !== roomId) {
        setNotice(`This mule is outfitted for ${roverOutfitType}. Sell that equipment in the ${roverOutfitType} room.`);
      } else if (roverOutfitType === roomId) {
        setSelectedOutfitType(roomId);
        setSellKind('equipment');
        setShowSellModal(true);
      } else {
        setSelectedOutfitType(roomId);
        setShowOutfitModal(true);
      }
    }
    // Nova Lounge (gambling room - ends turn)
    else if (roomId === 'nova_lounge') {
      console.log('🎰 Entered Nova Lounge');
      if (onNovaLounge) {
        onNovaLounge();
        return;
      }
      onClose();
    }
    // Other rooms
    else {
      if (room) {
        onEnterRoom(room.type);
      }
    }
  }, [hasRover, roverOutfitType, attachedRover, onEnterRoom, onClose, refreshWallet]);
  
  /**
   * Handle exiting the store
   * If player has an outfitted rover, trigger rover deployment mode
   * @param exitDirection - Direction the player is exiting from
   */
  const handleExitStore = useCallback((exitDirection: 'top' | 'bottom' | 'left' | 'right' = 'bottom') => {
    console.log('🚪 Exiting store via:', exitDirection, { hasRover, roverOutfitType, currentMoney });
    
    if (hasRover && roverOutfitType) {
      // Player has an outfitted rover - trigger deployment mode
      console.log('🚀 Exiting with outfitted rover:', roverOutfitType, 'via exit:', exitDirection);
      onExitWithRover({
        hasRover: true,
        outfitType: roverOutfitType,
        money: currentMoney,
        exitDirection,
      });
    } else if (hasRover && !roverOutfitType) {
      onExitWithRover({
        hasRover: true,
        outfitType: null,
        money: currentMoney,
        exitDirection,
      });
    } else {
      // No rover - just close (pass exit direction for position update)
      onClose(exitDirection);
    }
  }, [hasRover, roverOutfitType, currentMoney, onExitWithRover, onClose, onNovaLounge]);
  
  /**
   * Check if avatar collides with a rectangle (room or wall)
   * @param ignoreRoomId - Optional room ID to ignore collision (for walking INTO room)
   */
  const checkCollision = useCallback((newX: number, newY: number, ignoreRoomId?: string | null): boolean => {
    const avatarRadius = AVATAR_SIZE / 2;
    
    // Check collision with outer walls
    if (newX - avatarRadius < FLOOR_MARGIN || 
        newX + avatarRadius > STORE_WIDTH - FLOOR_MARGIN ||
        newY - avatarRadius < FLOOR_MARGIN || 
        newY + avatarRadius > STORE_HEIGHT - FLOOR_MARGIN) {
      return true; // Collision with outer wall
    }
    
    // Check collision with rooms (except target room)
    for (const room of ROOMS) {
      // Skip collision check for target room (allow walking INTO it)
      if (ignoreRoomId && room.id === ignoreRoomId) {
        continue;
      }
      
      if (newX + avatarRadius > room.x &&
          newX - avatarRadius < room.x + room.width &&
          newY + avatarRadius > room.y &&
          newY - avatarRadius < room.y + room.height) {
        return true; // Collision with room
      }
    }
    
    return false; // No collision
  }, []);
  
  /**
   * Check if avatar is near a room (within interaction distance)
   */
  const getNearbyRoom = useCallback((x: number, y: number): Room | null => {
    const interactionDistance = 50; // pixels
    
    for (const room of ROOMS) {
      const roomCenterX = room.x + room.width / 2;
      const roomCenterY = room.y + room.height / 2;
      const distance = Math.sqrt(
        Math.pow(x - roomCenterX, 2) + Math.pow(y - roomCenterY, 2)
      );
      
      if (distance < interactionDistance + AVATAR_SIZE) {
        return room;
      }
    }
    
    return null;
  }, []);
  
  /**
   * Check if avatar is near an exit
   */
  const isNearExit = useCallback((x: number, y: number): boolean => {
    const exitDistance = 60; // pixels
    
    for (const exit of EXITS) {
      const exitCenterX = exit.x + exit.width / 2;
      const exitCenterY = exit.y + exit.height / 2;
      const distance = Math.sqrt(
        Math.pow(x - exitCenterX, 2) + Math.pow(y - exitCenterY, 2)
      );
      
      if (distance < exitDistance) {
        return true;
      }
    }
    
    return false;
  }, []);
  
  /**
   * Update avatar position based on click/tap target (mouse/touch only)
   */
  const updateAvatarPosition = useCallback(() => {
    let dx = 0;
    let dy = 0;
    let moving = false;
    let newDirection = avatarDirection;
    
    // Click/tap-to-move
    if (targetPos && targetPosRef.current) {
      const distX = targetPos.x - avatarPos.x;
      const distY = targetPos.y - avatarPos.y;
      const distance = Math.sqrt(distX * distX + distY * distY);
      
      // If close enough to target, stop and check for room/exit interaction
      if (distance < AVATAR_SPEED * 2) {
        console.log('🎯 Reached target position');
        
        // Clear BOTH state and ref for immediate effect
        setTargetPos(null);
        targetPosRef.current = null;
        setIsWalking(false);
        
        // If we were walking to a room, auto-interact
        if (targetRoomRef.current) {
          handleRoomReached(targetRoomRef.current);
        }
        
        // If we were walking to an exit, trigger exit
        if (targetExitRef.current) {
          console.log('🚪 Reached exit:', targetExitRef.current);
          const exitDirection = targetExitRef.current as 'top' | 'bottom' | 'left' | 'right';
          targetExitRef.current = null;
          handleExitStore(exitDirection);
        }
        
        return;
      }
      
      // Move towards target
      const angle = Math.atan2(distY, distX);
      dx = Math.cos(angle) * AVATAR_SPEED;
      dy = Math.sin(angle) * AVATAR_SPEED;
      moving = true;
      
      // Set direction based on angle
      const absAngle = Math.abs(angle);
      if (absAngle < Math.PI / 4) {
        newDirection = 'right';
      } else if (absAngle > 3 * Math.PI / 4) {
        newDirection = 'left';
      } else if (angle > 0) {
        newDirection = 'down';
      } else {
        newDirection = 'up';
      }
    }
    
    setIsWalking(moving);
    
    if (moving) {
      setAvatarDirection(newDirection);
      
      setAvatarPos(prev => {
        const newX = prev.x + dx;
        const newY = prev.y + dy;
        
        // Check collision (ignore target room if walking to it)
        if (checkCollision(newX, newY, targetRoomRef.current)) {
          // If click-to-move hit a wall, cancel target
          if (targetPos) {
            setTargetPos(null);
            targetRoomRef.current = null;
            targetExitRef.current = null;
          }
          return prev; // Don't move if collision
        }
        
        // Update rover position (follows with slight delay) - only if player has purchased one
        if (hasRover) {
          // Position rover BEHIND avatar based on direction
          let roverOffsetX = 0;
          let roverOffsetY = 0;
          
          switch (newDirection) {
            case 'up':
              roverOffsetX = 0;
              roverOffsetY = 35; // Behind = below when moving up
              break;
            case 'down':
              roverOffsetX = 0;
              roverOffsetY = -35; // Behind = above when moving down
              break;
            case 'left':
              roverOffsetX = 35; // Behind = to the right when moving left
              roverOffsetY = 0;
              break;
            case 'right':
              roverOffsetX = -35; // Behind = to the left when moving right
              roverOffsetY = 0;
              break;
          }
          
          setRoverPos({ x: prev.x + roverOffsetX, y: prev.y + roverOffsetY });
        }
        
        return { x: newX, y: newY };
      });
      
      // Check for nearby room
      const nearby = getNearbyRoom(avatarPos.x + dx, avatarPos.y + dy);
      setHoveredRoom(nearby ? nearby.id : null);
      
      // Check for exit
      if (isNearExit(avatarPos.x + dx, avatarPos.y + dy)) {
        setHoveredRoom('exit');
      }
    }
  }, [avatarDirection, avatarPos, targetPos, hasRover, handleRoomReached, checkCollision, getNearbyRoom, isNearExit]);
  
  /**
   * Animation frame cycling
   */
  useEffect(() => {
    if (!isWalking) {
      // Idle animation (subtle weight shift)
      const interval = setInterval(() => {
        setAnimationFrame(prev => (prev === 0 ? 1 : 0));
      }, 3000);
      return () => clearInterval(interval);
    } else {
      // Walking animation
      const interval = setInterval(() => {
        setAnimationFrame(prev => (prev + 1) % 4);
      }, 125);
      return () => clearInterval(interval);
    }
  }, [isWalking]);
  
  /**
   * Keyboard event handlers
   */
  // No keyboard controls - mouse/touch only for mobile compatibility
  
  /**
   * Draw the store map
   */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, STORE_WIDTH, STORE_HEIGHT);
    
    // Background
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, STORE_WIDTH, STORE_HEIGHT);
    
    // Floor (walkable area)
    ctx.fillStyle = COLORS.floor;
    ctx.fillRect(FLOOR_MARGIN, FLOOR_MARGIN, STORE_WIDTH - FLOOR_MARGIN * 2, STORE_HEIGHT - FLOOR_MARGIN * 2);
    ctx.strokeStyle = COLORS.wall;
    ctx.lineWidth = 3;
    ctx.strokeRect(FLOOR_MARGIN, FLOOR_MARGIN, STORE_WIDTH - FLOOR_MARGIN * 2, STORE_HEIGHT - FLOOR_MARGIN * 2);
    
    // Draw exits
    ctx.fillStyle = COLORS.door;
    ctx.strokeStyle = COLORS.wall;
    ctx.lineWidth = 2;
    EXITS.forEach(exit => {
      ctx.fillRect(exit.x, exit.y, exit.width, exit.height);
      ctx.strokeRect(exit.x, exit.y, exit.width, exit.height);
      
      // Exit text
      ctx.fillStyle = COLORS.background;
      ctx.font = '14px Monaco, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (exit.name === 'top' || exit.name === 'bottom') {
        ctx.fillText('EXIT/IN', exit.x + exit.width / 2, exit.y + exit.height / 2);
      } else {
        // Vertical text for left/right
        ctx.save();
        ctx.translate(exit.x + exit.width / 2, exit.y + exit.height / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText('EXIT', 0, 0);
        ctx.restore();
      }
    });
    
    // Draw rooms
    ROOMS.forEach(room => {
      const isHovered = hoveredRoom === room.id;
      
      ctx.fillStyle = isHovered ? '#1A4A78' : COLORS.roomBg;
      ctx.fillRect(room.x, room.y, room.width, room.height);
      ctx.strokeStyle = isHovered ? COLORS.interact : COLORS.wall;
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.strokeRect(room.x, room.y, room.width, room.height);
      
      // Room name
      ctx.fillStyle = COLORS.text;
      ctx.font = '14px Monaco, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const lines = room.name.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, room.x + room.width / 2, room.y + 18 + i * 16);
      });
      if (room.id === 'rover_sales') {
        ctx.fillStyle = mulesLeft > 0 ? '#7DFFB3' : '#FF8A80';
        ctx.font = '12px Monaco, monospace';
        ctx.fillText(`${mulesLeft} MULES`, room.x + room.width / 2, room.y + room.height - 14);
      }
      
      // Interaction prompt (removed - click-to-move handles interaction)
      // No need to show "Tap / Press E" text anymore
    });
    
    // Draw avatar
    if (!spritesLoading && avatarSprites && currentPlayerNumber) {
      const sprite = avatarSprites[currentPlayerNumber]?.[animationFrame];
      if (sprite) {
        ctx.drawImage(
          sprite,
          avatarPos.x - AVATAR_SIZE / 2,
          avatarPos.y - AVATAR_SIZE / 2,
          AVATAR_SIZE,
          AVATAR_SIZE
        );
      }
    } else {
      // Fallback circle
      ctx.fillStyle = '#FF6600';
      ctx.beginPath();
      ctx.arc(avatarPos.x, avatarPos.y, AVATAR_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = COLORS.text;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    
    // Draw rover (only if purchased in this store visit)
    if (hasRover) {
      if (!roverSpritesLoading && roverSprites) {
        const roverSprite = roverSprites[animationFrame];
        if (roverSprite) {
          ctx.drawImage(
            roverSprite,
            roverPos.x - AVATAR_SIZE / 2,
            roverPos.y - AVATAR_SIZE / 2,
            AVATAR_SIZE,
            AVATAR_SIZE
          );
        }
      } else {
        // Fallback rectangle
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(roverPos.x - 12, roverPos.y - 12, 24, 24);
        ctx.strokeStyle = COLORS.text;
        ctx.lineWidth = 2;
        ctx.strokeRect(roverPos.x - 12, roverPos.y - 12, 24, 24);
      }
      
      // Dotted line (harness)
      ctx.strokeStyle = COLORS.text;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(avatarPos.x, avatarPos.y);
      ctx.lineTo(roverPos.x, roverPos.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // Draw controls legend
    ctx.font = '12px Monaco, monospace';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Tap to move', STORE_WIDTH / 2, STORE_HEIGHT / 2 + 36);
    if (notice) {
      ctx.fillStyle = '#FFC107';
      ctx.font = '13px Monaco, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(notice, STORE_WIDTH / 2, STORE_HEIGHT / 2 + 58);
    }
  }, [avatarPos, roverPos, hoveredRoom, hasRover, animationFrame, avatarSprites, roverSprites, spritesLoading, roverSpritesLoading, currentPlayerNumber, notice, mulesLeft]);
  
  /**
   * Animation loop
   */
  useEffect(() => {
    if (!isOpen) return;
    
    const animate = () => {
      updateAvatarPosition();
      draw();
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, updateAvatarPosition, draw]);
  
  if (!isOpen) return null;
  
  /**
   * Handle canvas click/tap to set target position or interact with rooms
   */
  const handleCanvasClick = async (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Disable interactions when watching AI
    if (isAITurn) {
      console.log('🤖 Watching AI player - interactions disabled');
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    
    console.log('🖱️ Store canvas clicked at:', { clickX, clickY });
    
    // Check if clicking on a room
    const clickedRoom = ROOMS.find(room => 
      clickX >= room.x && 
      clickX <= room.x + room.width &&
      clickY >= room.y && 
      clickY <= room.y + room.height
    );
    
    if (clickedRoom) {
      console.log('🚪 Clicked on room:', clickedRoom.name);
      // Set target to walk near the room (center of room)
      const targetX = clickedRoom.x + clickedRoom.width / 2;
      const targetY = clickedRoom.y + clickedRoom.height / 2;
      console.log('✅ Walking to room:', clickedRoom.name, { targetX, targetY });
      const newTarget = { x: targetX, y: targetY };
      setTargetPos(newTarget);
      targetPosRef.current = newTarget; // Set ref immediately
      targetRoomRef.current = clickedRoom.id; // Remember which room we're walking to
      targetExitRef.current = null; // Clear exit target
      return;
    }
    
    // Check if clicking on an exit
    const clickedExit = EXITS.find(exit => 
      clickX >= exit.x && 
      clickX <= exit.x + exit.width &&
      clickY >= exit.y && 
      clickY <= exit.y + exit.height
    );
    
    if (clickedExit) {
      console.log('🚪 Clicked on exit:', clickedExit.name);
      // Walk to a position INSIDE the walkable area near the exit
      // Exit centers are outside FLOOR_MARGIN, so we need to calculate a safe target
      let targetX = clickedExit.x + clickedExit.width / 2;
      let targetY = clickedExit.y + clickedExit.height / 2;
      
      // Adjust target to be just inside the walkable area
      const safeMargin = FLOOR_MARGIN + AVATAR_SIZE / 2 + 5; // Stay inside with buffer
      switch (clickedExit.name) {
        case 'left':
          targetX = safeMargin;
          targetY = STORE_HEIGHT / 2; // Center vertically
          break;
        case 'right':
          targetX = STORE_WIDTH - safeMargin;
          targetY = STORE_HEIGHT / 2;
          break;
        case 'top':
          targetX = STORE_WIDTH / 2;
          targetY = safeMargin;
          break;
        case 'bottom':
          targetX = STORE_WIDTH / 2;
          targetY = STORE_HEIGHT - safeMargin;
          break;
      }
      
      console.log('🚶 Walking to exit:', clickedExit.name, { targetX, targetY });
      const newTarget = { x: targetX, y: targetY };
      setTargetPos(newTarget);
      targetPosRef.current = newTarget;
      targetRoomRef.current = null; // Clear room target
      targetExitRef.current = clickedExit.name; // Set exit target
      return;
    }
    
    // Check if target is walkable (not colliding)
    if (checkCollision(clickX, clickY)) {
      console.log('⛔ Cannot walk to:', { clickX, clickY }, '(collision)');
      return;
    }
    
    // Set target position for click-to-move
    console.log('✅ Setting target position:', { clickX, clickY });
    const newTarget = { x: clickX, y: clickY };
    setTargetPos(newTarget);
    targetPosRef.current = newTarget; // Set ref immediately
    targetRoomRef.current = null; // Clear room target
    targetExitRef.current = null; // Clear exit target
  };
  
  // Get action display label
  const getAIActionLabel = (action: string) => {
    const cyclePrefix = aiRoverCycle > 1 ? `[Rover #${aiRoverCycle}] ` : '';
    switch (action) {
      case 'entering_store': return `${cyclePrefix}🚶 Walking into store...`;
      case 'buying_rover': return `${cyclePrefix}🤖 Purchasing rover...`;
      case 'outfitting': return `${cyclePrefix}🔧 Outfitting with ${aiOutfitType?.toUpperCase() || 'equipment'}...`;
      case 'exiting_store': return `${cyclePrefix}🚪 Exiting store...`;
      case 'returning_to_store': return `${cyclePrefix}🚶 Returning to store...`;
      case 'visiting_lounge': return '🎰 Trying luck at Nova Lounge...';
      default: return '👀 Browsing...';
    }
  };
  
  return (
    <div className="store-map-overlay">
      {/* AI Spectating Banner */}
      {isAITurn && (
        <div className="ai-spectate-banner">
          <span className="ai-spectate-icon">🤖</span>
          <span className="ai-spectate-text">
            Watching <strong>{aiPlayerName}</strong>
          </span>
          <span className="ai-spectate-action">{getAIActionLabel(aiAction)}</span>
        </div>
      )}
      
      <div className="store-status">
        <div className={`store-status-time${shownTime <= 5 ? ' is-critical' : shownTime <= 10 ? ' is-warning' : ''}`}>
          TIME: {`${Math.floor(shownTime / 60).toString().padStart(2, '0')}:${Math.floor(shownTime % 60).toString().padStart(2, '0')}`}
        </div>
        <div className={`store-status-bar${shownTime <= 5 ? ' is-critical' : shownTime <= 10 ? ' is-warning' : shownTime <= 15 ? ' is-low' : ''}`}>
          <span style={{ width: `${Math.max(0, Math.min(1, turnDuration ? shownTime / turnDuration : 0)) * 100}%` }} />
        </div>
        <div>MONEY: {currentMoney} CT</div>
        <div>MULES: {mulesLeft}</div>
        <div className={`store-status-rover${hasRover && roverOutfitType ? ' is-ready' : ''}`}>
          ROVER: {hasRover ? (roverOutfitType ? roverOutfitType.toUpperCase() : 'UNOUTFITTED') : 'NONE'}
        </div>
      </div>

      <canvas
        ref={canvasRef}
        width={STORE_WIDTH}
        height={STORE_HEIGHT}
        className="store-map-canvas"
        onClick={handleCanvasClick}
        style={{ cursor: isAITurn ? 'not-allowed' : 'pointer' }}
      />
      
      {/* Rover Purchase Modal */}
      {showPurchaseModal && (
        <div className="store-modal-overlay" onClick={() => setShowPurchaseModal(false)}>
          <div className="store-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🚀 Purchase Rover</h2>
            <p className="store-modal-description">
              A blank rover chassis ready for outfitting.
            </p>
            <div className="store-modal-price">
              <span className="price-label">Mules in store:</span>
              <span className="price-value">{mulesLeft}</span>
            </div>
            <div className="store-modal-price">
              <span className="price-label">Cost:</span>
              <span className="price-value">{ROVER_BASE_COST} CT</span>
            </div>
            <div className="store-modal-balance">
              <span>Your Balance:</span>
              <span className={currentMoney >= ROVER_BASE_COST ? 'balance-ok' : 'balance-low'}>
                {currentMoney} CT
              </span>
            </div>
            <div className="store-modal-actions">
              <button
                className="btn-purchase"
                onClick={handlePurchaseRover}
                disabled={currentMoney < ROVER_BASE_COST || mulesLeft <= 0}
              >
                {mulesLeft <= 0 ? 'Out of mules' : currentMoney >= ROVER_BASE_COST ? 'Purchase' : 'Insufficient Funds'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowPurchaseModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Outfit Modal */}
      {showOutfitModal && selectedOutfitType && (
        <div className="store-modal-overlay" onClick={() => setShowOutfitModal(false)}>
          <div className="store-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🔧 Outfit Rover</h2>
            <p className="store-modal-description">
              Outfit Type: <strong>{selectedOutfitType.toUpperCase()}</strong>
            </p>
            {roverOutfitType && (
              <p className="store-modal-warning">
                ⚠️ Rover is currently outfitted with <strong>{roverOutfitType.toUpperCase()}</strong>.
                <br />Re-outfitting will cost again.
              </p>
            )}
            <div className="store-modal-price">
              <span className="price-label">Cost:</span>
              <span className="price-value">{OUTFIT_COSTS[selectedOutfitType]} CT</span>
            </div>
            <div className="store-modal-balance">
              <span>Your Balance:</span>
              <span className={currentMoney >= OUTFIT_COSTS[selectedOutfitType] ? 'balance-ok' : 'balance-low'}>
                {currentMoney} CT
              </span>
            </div>
            <div className="store-modal-actions">
              <button
                className="btn-purchase"
                onClick={() => handleOutfitRover(selectedOutfitType)}
                disabled={currentMoney < OUTFIT_COSTS[selectedOutfitType]}
              >
                {currentMoney >= OUTFIT_COSTS[selectedOutfitType] ? 'Outfit' : 'Insufficient Funds'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowOutfitModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sell Rover Modal */}
      {showSellModal && (
        <div className="store-modal-overlay" onClick={() => setShowSellModal(false)}>
          <div className="store-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{sellKind === 'equipment' ? 'Sell equipment' : 'Sell mule'}</h2>
            <p className="store-modal-description">
              {sellKind === 'equipment'
                ? `Unload the ${String(roverOutfitType || '').toUpperCase()} equipment. The mule stays with you, bare.`
                : 'Sell this bare mule back to the store.'}
            </p>
            <div className="store-modal-price">
              <span className="price-label">Market price:</span>
              <span className="price-value sell-price">
                {sellKind === 'equipment' ? OUTFIT_COSTS[roverOutfitType || ''] || 0 : ROVER_BASE_COST} CT
              </span>
            </div>
            <div className="store-modal-balance">
              <span>Your Balance:</span>
              <span className="balance-ok">{currentMoney} CT</span>
            </div>
            <div className="store-modal-actions">
              <button
                className="btn-sell"
                onClick={handleSellRover}
              >
                Sell for {sellKind === 'equipment' ? OUTFIT_COSTS[roverOutfitType || ''] || 0 : ROVER_BASE_COST} CT
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowSellModal(false)}
              >
                Keep Rover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreMap;

